import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

import { useAuth } from "../context/AuthContext";

const STATUS_META = {
  received: { label: "New", cls: "bg-[#B7791F]/10 text-[#8a5d14] border-[#B7791F]/30" },
  acknowledged: { label: "Acknowledged", cls: "bg-primary/10 text-primary border-primary/30" },
  investigating: { label: "Investigating", cls: "bg-[#2F6B4F]/10 text-[#2F6B4F] border-[#2F6B4F]/30" },
  resolved: { label: "Resolved", cls: "bg-stone-100 text-stone-500 border-stone-200" },
  void: { label: "Void", cls: "bg-stone-50 text-stone-400 border-stone-200 line-through" },
};
const TYPES = ["client", "client (via caregiver)", "family/representative", "caregiver", "community", "anonymous", "other"];
const VIA = ["phone", "in person", "written/letter", "wrap-up survey", "family portal", "other"];
const EMPTY = { received_date: "", occurrence_date: "", received_via: "phone", client_id: "", client_name: "",
  complainant_name: "", complainant_type: "client", complainant_contact: "", employee_name: "", summary: "", details: "" };

function todayISO() { return new Date().toISOString().slice(0, 10); }

function clientLabel(c) {
  return c.full_name || c.name || c.forms?.intake?.full_name || c.client_code || "Unnamed";
}

function ComplaintDialog({ open, onClose, onSaved, initial, clients }) {
  const { user: me } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [more, setMore] = useState(false);
  useEffect(() => { if (open) { setF({ ...EMPTY, received_date: todayISO(), received_by: me?.name || "", ...(initial || {}) }); setQ(""); setMore(false); } }, [open, initial]);
  const set = (k) => (e) => setF((c) => ({ ...c, [k]: e.target.value }));

  const sortedClients = [...clients].sort((a, b) => clientLabel(a).localeCompare(clientLabel(b)));
  const shownClients = q.trim()
    ? sortedClients.filter((c) => clientLabel(c).toLowerCase().includes(q.trim().toLowerCase()))
    : sortedClients;

  const pickClient = (e) => {
    const c = clients.find((x) => x.id === e.target.value);
    setF((cur) => ({ ...cur, client_id: c?.id || "", client_name: c ? clientLabel(c) : cur.client_name }));
  };

  const save = async () => {
    if (!f.summary.trim()) { toast.error("One line about the complaint is required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/complaints", f);
      toast.success("Complaint logged");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="complaint-dialog">
        <DialogHeader><DialogTitle>Log a complaint</DialogTitle></DialogHeader>
        <div className="mt-2 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Date received</span>
              <Input type="date" value={f.received_date} onChange={set("received_date")} className="mt-1 border-stone-300" data-testid="complaint-date" />
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Who is complaining</span>
              <select value={f.complainant_type} onChange={set("complainant_type")} className="mt-1 h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="complaint-type">
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Client</span>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type to find the client…"
              className="mt-1 border-stone-300" data-testid="complaint-client-search" />
            <select value={f.client_id} onChange={pickClient} className="mt-1 h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="complaint-client">
              <option value="">— not tied to a client record —</option>
              {shownClients.map((c) => <option key={c.id} value={c.id}>{clientLabel(c)}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">What is the complaint? (one line)</span>
            <Input value={f.summary} onChange={set("summary")} placeholder="e.g. Caregiver arrived late twice this week" className="mt-1 border-stone-300" data-testid="complaint-summary" />
          </label>
          <button type="button" onClick={() => setMore(!more)} className="text-xs font-semibold text-primary underline" data-testid="complaint-more-fields">
            {more ? "− fewer fields" : "+ more detail (who told us, caregiver involved, what happened)"}
          </button>
          {more && (
            <div className="space-y-3 rounded-lg border border-stone-100 bg-stone-50/60 p-3" data-testid="complaint-more-panel">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Who told us</span>
                  <Input value={f.complainant_name} onChange={set("complainant_name")} className="mt-1 border-stone-300 bg-white" data-testid="complaint-complainant" />
                </label>
                <label className="block">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Taken by (office)</span>
                  <Input value={f.received_by} onChange={set("received_by")} className="mt-1 border-stone-300 bg-white" data-testid="complaint-taken-by" />
                </label>
                <label className="block">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Caregiver involved</span>
                  <Input value={f.employee_name} onChange={set("employee_name")} className="mt-1 border-stone-300 bg-white" data-testid="complaint-employee" />
                </label>
                <label className="block">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Date it happened</span>
                  <Input type="date" value={f.occurrence_date} onChange={set("occurrence_date")} className="mt-1 border-stone-300 bg-white" data-testid="complaint-occurrence" />
                </label>
                <label className="block">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Received via</span>
                  <select value={f.received_via} onChange={set("received_via")} className="mt-1 h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="complaint-via">
                    {VIA.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Full details</span>
                <textarea value={f.details} onChange={set("details")} rows={3} className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="complaint-details" />
              </label>
            </div>
          )}
          <Button onClick={save} disabled={busy || !f.summary.trim()} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="complaint-save">
            {busy ? "Saving…" : "Log complaint"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ComplaintRow({ c, onChanged }) {
  const [open, setOpen] = useState(false);
  const [resolution, setResolution] = useState(c.resolution || "");
  const [notes, setNotes] = useState(c.investigation_notes || "");
  const [satisfied, setSatisfied] = useState(c.consumer_satisfied || "");
  const [takenBy, setTakenBy] = useState(c.received_by || "");
  const [busy, setBusy] = useState(false);
  const meta = STATUS_META[c.status] || STATUS_META.received;
  const isOpen = !["resolved", "void"].includes(c.status);
  const overdue = isOpen && c.acknowledge_by && c.acknowledge_by < todayISO() && c.status === "received";
  useEffect(() => { setResolution(c.resolution || ""); setNotes(c.investigation_notes || ""); setSatisfied(c.consumer_satisfied || ""); setTakenBy(c.received_by || ""); }, [c]);

  const patch = async (body) => {
    setBusy(true);
    try {
      await api.put(`/admin/complaints/${c.id}`, body);
      toast.success("Saved");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="rounded-lg border border-stone-200 bg-white px-4 py-3" data-testid={`complaint-${c.id}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-xs font-semibold text-stone-500">{c.received_date}</span>
        {c.client_id ? (
          <Link to={`/clients/${c.client_id}`} className="text-sm font-semibold text-primary hover:underline" data-testid={`complaint-client-link-${c.id}`}>{c.client_name}</Link>
        ) : (
          <span className="text-sm font-semibold text-stone-900">{c.client_name || "—"}</span>
        )}
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${meta.cls}`} data-testid={`complaint-status-${c.id}`}>{meta.label}</span>
        {overdue && <span className="text-[10px] font-bold text-[#C0392B]" data-testid={`complaint-overdue-${c.id}`}>needs acknowledgment</span>}
        {c.consumer_satisfied && c.status === "resolved" && (
          <span className={`text-[10px] font-bold ${c.consumer_satisfied === "yes" ? "text-[#1e8449]" : "text-[#8a5d14]"}`} data-testid={`complaint-satisfaction-${c.id}`}>
            · consumer {c.consumer_satisfied === "yes" ? "satisfied ✓" : c.consumer_satisfied}
          </span>
        )}
        <button onClick={() => setOpen(!open)} className="ml-auto text-xs font-semibold text-primary underline" data-testid={`complaint-expand-${c.id}`}>
          {open ? "Close" : isOpen ? "Work it" : "Edit"}
        </button>
      </div>
      <p className="mt-1 text-sm text-stone-700">{c.summary}</p>
      {open && (
        <div className="mt-3 space-y-3 rounded-lg border border-stone-100 bg-stone-50/60 p-3" data-testid={`complaint-work-${c.id}`}>
          {c.details && c.details !== c.summary && <p className="whitespace-pre-wrap text-xs text-stone-600">{c.details}</p>}
          <div className="flex flex-wrap gap-2">
            {[["acknowledged", "Acknowledged"], ["investigating", "Investigating"], ["resolved", "Resolved"], ["void", "Void (logged in error)"]].map(([s, label]) => (
              <button key={s} onClick={() => patch({ status: s, investigation_notes: notes, resolution, consumer_satisfied: satisfied })} disabled={busy || c.status === s}
                className={`rounded-full border px-3 py-1 text-[11px] font-semibold disabled:opacity-40 ${c.status === s ? "border-primary bg-primary/10 text-primary" : "border-stone-300 text-stone-600 hover:border-primary/50"}`}
                data-testid={`complaint-set-${s}-${c.id}`}>{label}</button>
            ))}
          </div>
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">What we did (outcome)</span>
            <textarea value={resolution} onChange={(e) => setResolution(e.target.value)} rows={2} className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid={`complaint-resolution-${c.id}`} />
          </label>
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Handled to the consumer's satisfaction?</span>
            <select value={satisfied} onChange={(e) => setSatisfied(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid={`complaint-satisfied-${c.id}`}>
              <option value="">— not answered yet —</option>
              <option value="yes">Yes</option>
              <option value="partially">Partially</option>
              <option value="no">No — follow-up needed</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Investigation notes (optional)</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid={`complaint-notes-${c.id}`} />
          </label>
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Taken by (office)</span>
            <input value={takenBy} onChange={(e) => setTakenBy(e.target.value)} className="mt-1 h-8 w-56 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid={`complaint-takenby-${c.id}`} />
          </label>
          <Button onClick={() => patch({ investigation_notes: notes, resolution, consumer_satisfied: satisfied, received_by: takenBy })} disabled={busy}
            className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`complaint-save-work-${c.id}`}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      )}
    </div>
  );
}

function QuickAdd({ clients, onSaved }) {
  const [q, setQ] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const sortedClients = [...clients].sort((a, b) => clientLabel(a).localeCompare(clientLabel(b)));
  const shown = q.trim() ? sortedClients.filter((c) => clientLabel(c).toLowerCase().includes(q.trim().toLowerCase())) : sortedClients;

  const save = async () => {
    if (!text.trim()) { toast.error("Type the complaint"); return; }
    setBusy(true);
    try {
      await api.post("/admin/complaints", { summary: text.trim(), client_id: clientId, client_name: clientName });
      toast.success("Logged — work the details whenever");
      setText(""); setClientId(""); setClientName(""); setQ("");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-primary/25 bg-primary/[0.03] px-4 py-3" data-testid="complaint-quick-add">
      <span className="text-xs font-bold uppercase tracking-wide text-primary">Quick log</span>
      <div className="relative">
        <Input value={clientName || q} onChange={(e) => { setQ(e.target.value); setClientId(""); setClientName(e.target.value); }}
          placeholder="Client (type to search)" className="h-9 w-52 border-stone-300 bg-white" data-testid="quick-add-client" />
        {q.trim() && !clientId && shown.length > 0 && (
          <div className="absolute z-20 mt-1 max-h-44 w-64 overflow-y-auto rounded-lg border border-stone-200 bg-white shadow-lg" data-testid="quick-add-client-list">
            {shown.slice(0, 8).map((c) => (
              <button key={c.id} type="button" onClick={() => { setClientId(c.id); setClientName(clientLabel(c)); setQ(""); }}
                className="block w-full px-3 py-1.5 text-left text-xs hover:bg-primary/5" data-testid={`quick-add-client-${c.id}`}>
                {clientLabel(c)}
              </button>
            ))}
          </div>
        )}
      </div>
      <Input value={text} onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") save(); }}
        placeholder="What was the complaint? (one line — details later)" className="h-9 min-w-0 flex-1 border-stone-300 bg-white" data-testid="quick-add-text" />
      <Button onClick={save} disabled={busy || !text.trim()} size="sm"
        className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="quick-add-save">
        {busy ? "…" : "Log it"}
      </Button>
    </div>
  );
}

function OutcomeRow({ c, onSaved }) {
  const [status, setStatus] = useState(c.status === "void" ? "void" : "resolved");
  const [resolution, setResolution] = useState(c.resolution || "");
  const [satisfied, setSatisfied] = useState(c.consumer_satisfied || "");
  const [busy, setBusy] = useState(false);
  const dirty = status !== c.status || resolution !== (c.resolution || "") || satisfied !== (c.consumer_satisfied || "");
  const save = async () => {
    if (status === "resolved" && !satisfied) { toast.error("Client satisfaction is required to mark resolved"); return; }
    setBusy(true);
    try {
      await api.put(`/admin/complaints/${c.id}`, { status, resolution, consumer_satisfied: satisfied });
      toast.success(`${c.client_name || "entry"}: saved`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="rounded-lg border border-stone-200 bg-white px-4 py-3" data-testid={`outcome-row-${c.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-stone-500">{c.received_date}</span>
        <span className="text-sm font-semibold text-stone-900">{c.client_name || "—"}</span>
        <span className="min-w-0 flex-1 truncate text-xs text-stone-600">{c.summary}</span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid={`outcome-status-${c.id}`}>
          {[["resolved", "Resolved"], ["received", "Still open"], ["acknowledged", "Acknowledged"], ["investigating", "Investigating"], ["void", "Void (not a complaint)"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input value={resolution} onChange={(e) => setResolution(e.target.value)}
          placeholder="What we did (outcome)"
          className="h-8 min-w-0 flex-1 rounded-md border border-stone-300 px-2 text-xs" data-testid={`outcome-resolution-${c.id}`} />
        <select value={satisfied} onChange={(e) => setSatisfied(e.target.value)}
          className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid={`outcome-satisfied-${c.id}`}>
          <option value="">Satisfied? —</option>
          <option value="yes">Yes</option>
          <option value="partially">Partially</option>
          <option value="no">No</option>
        </select>
        <Button onClick={save} disabled={busy || !dirty} size="sm"
          className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`outcome-save-${c.id}`}>
          {busy ? "…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

export default function ComplaintsPage() {
  const [complaints, setComplaints] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [showCandidates, setShowCandidates] = useState(false);
  const [clients, setClients] = useState([]);
  const [adding, setAdding] = useState(false);
  const [initial, setInitial] = useState(null);
  const [outcomeMode, setOutcomeMode] = useState(false);

  const load = () => {
    api.get("/admin/complaints").then((r) => setComplaints(r.data.complaints)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/complaints/note-candidates").then((r) => setCandidates(r.data.candidates)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients((r.data || []).filter((c) => !c.is_duplicate))).catch(() => {});
  }, []);

  const download = async (path, filename, openTab = false) => {
    // open the tab synchronously — window.open after an await gets popup-blocked
    const w = openTab ? window.open("", "_blank") : null;
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (openTab) { if (w) w.location.href = url; return; }
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
    } catch (e) {
      if (w) w.close();
      toast.error(formatApiError(e));
    }
  };

  const addFromNote = (n) => {
    const firstLine = (n.excerpt || "").split("\n").map((x) => x.trim()).filter(Boolean)[0] || "";
    setInitial({ received_date: n.event_date || todayISO(), complainant_name: n.contact || "",
      summary: firstLine.slice(0, 200),
      details: `From office note ${n.event_date || ""} (${n.contact || "unknown contact"}):\n${n.excerpt}`,
      source_note_id: n.note_id });
    setAdding(true);
  };

  const open = (complaints || []).filter((c) => !["resolved", "void"].includes(c.status));
  const closed = (complaints || []).filter((c) => ["resolved", "void"].includes(c.status));

  return (
    <div className="mx-auto max-w-4xl px-4 py-8" data-testid="complaints-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Complaints log</h1>
          <p className="mt-1 text-sm text-stone-500">Acknowledge within 2 business days, resolve within 5. Entries are never deleted.</p>
        </div>
        <div className="flex gap-2">
          <Button variant={outcomeMode ? "default" : "outline"} onClick={() => setOutcomeMode(!outcomeMode)}
            className={`rounded-full ${outcomeMode ? "bg-primary text-primary-foreground" : ""}`} data-testid="complaints-outcome-mode">
            {outcomeMode ? "Done entering outcomes" : "Enter outcomes (bulk)"}
          </Button>
          <Button onClick={() => download("/admin/complaints/print.pdf", "", true)} variant="outline" className="rounded-full" data-testid="complaints-print">Print log</Button>
          <Button onClick={() => { setInitial(null); setAdding(true); }} variant="outline" className="rounded-full" data-testid="complaints-add">Full form</Button>
        </div>
      </div>

      <QuickAdd clients={clients} onSaved={load} />

      {complaints !== null && open.length === 0 && closed.length > 0 && (
        <div className="mt-4 rounded-xl border border-[#27AE60]/30 bg-[#27AE60]/[0.05] px-4 py-3" data-testid="complaints-caught-up">
          <p className="text-sm font-semibold text-[#1e8449]">All caught up</p>
          <p className="text-xs text-stone-500">No open complaints. This is an active, ongoing log — new complaints land here from wrap-ups, call notes, or the quick log above, and the full history prints below.</p>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="complaints-summary">
        <span className="rounded-full border border-stone-200 bg-white px-3 py-1 text-xs font-semibold text-stone-600" data-testid="complaints-open-count">{open.length} open</span>
        <span className="rounded-full border border-stone-200 bg-white px-3 py-1 text-xs font-semibold text-stone-500">{closed.length} resolved/void</span>
        {candidates.length > 0 && (
          <button onClick={() => setShowCandidates(!showCandidates)}
            className="rounded-full border border-[#B7791F]/40 bg-[#B7791F]/10 px-3 py-1 text-xs font-semibold text-[#8a5d14] hover:bg-[#B7791F]/20"
            data-testid="candidates-toggle">
            {showCandidates ? "Hide" : "Review"} {candidates.length} note{candidates.length !== 1 ? "s" : ""} that might be complaints
          </button>
        )}
      </div>

      {showCandidates && candidates.length > 0 && (
        <div className="mt-4 rounded-xl border border-[#B7791F]/25 bg-[#B7791F]/[0.04] p-4" data-testid="complaint-candidates">
          <p className="text-xs text-stone-500">These office notes sound complaint-ish. Log the real ones, dismiss the rest — the detector learns from dismissals.</p>
          <div className="mt-2 space-y-2">
            {candidates.map((n) => (
              <div key={n.note_id} className="rounded-lg border border-stone-200 bg-white px-4 py-2.5" data-testid={`candidate-${n.note_id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-stone-500">{n.event_date || "no date"}</span>
                  <span className="text-xs font-semibold text-stone-700">{n.contact || "unknown contact"}</span>
                  <span className="ml-auto flex gap-2">
                    <button onClick={() => addFromNote(n)} className="rounded-full bg-primary px-3 py-1 text-[11px] font-semibold text-primary-foreground hover:bg-stone-900" data-testid={`candidate-add-${n.note_id}`}>Add to log</button>
                    <button onClick={async () => {
                      try {
                        await api.post("/admin/complaints/note-feedback", { note_id: n.note_id, verdict: "not_complaint" });
                        toast.success("Dismissed — the detector learned from it");
                        load();
                      } catch (e) { toast.error(formatApiError(e)); }
                    }} className="rounded-full border border-stone-300 px-3 py-1 text-[11px] font-semibold text-stone-600" data-testid={`candidate-dismiss-${n.note_id}`}>Not a complaint</button>
                  </span>
                </div>
                <p className="mt-1 text-xs text-stone-600">{n.excerpt}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5 space-y-2">
        {complaints === null ? (
          <p className="text-sm text-stone-400">Loading…</p>
        ) : complaints.length === 0 ? (
          <div className="rounded-xl border border-dashed border-stone-300 bg-white px-6 py-10 text-center" data-testid="complaints-empty">
            <p className="text-sm font-semibold text-stone-600">No complaints logged</p>
            <p className="mt-1 text-xs text-stone-400">When a client or family raises a concern, log it — the dates and record keep themselves.</p>
          </div>
        ) : outcomeMode ? (
          [...open, ...closed].map((c) => <OutcomeRow key={c.id} c={c} onSaved={load} />)
        ) : (
          <>
            {open.map((c) => <ComplaintRow key={c.id} c={c} onChanged={load} />)}
            {closed.length > 0 && (
              <p className="pt-3 text-[11px] font-semibold uppercase tracking-wide text-stone-400">Resolved &amp; void</p>
            )}
            {closed.map((c) => <ComplaintRow key={c.id} c={c} onChanged={load} />)}
          </>
        )}
      </div>

      <ComplaintDialog open={adding} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} initial={initial} clients={clients} />
    </div>
  );
}
